import Header from "./Header";
import Footer from "./Footer";
import { Outlet } from "react-router-dom";
import GlobalModal from "./GlobalModal";
import ChatAssistant from "./ChatAssistant";
import { useState, useEffect, useRef, useCallback } from "react";
import MessageManager from "./Message";
import { getUserProfile } from "../../services/sharedService";
import { sendVerificationEmailApi } from "../../services/authService";

const ENABLE_CHAT_ASSISTANT = false; // Feature flag to toggle ChatAssistant visibility

function Layout() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const bannerRef = useRef(null);
  const profileRef = useRef(null);

  const toggleChat = () => setIsChatOpen((prev) => !prev);

  // Keep the global banner directly below the header,
  // even when the header height changes because text wraps.
  useEffect(() => {
    const header = document.querySelector(".main-header");

    if (!header) return undefined;

    const updateHeaderHeight = () => {
      const headerHeight = Math.ceil(header.getBoundingClientRect().height);

      document.documentElement.style.setProperty(
        "--maya-header-height",
        `${headerHeight}px`,
      );
    };

    // Set the correct value immediately.
    updateHeaderHeight();

    // Recalculate whenever the header changes height
    // (long username/org name, responsive layout, etc.).
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(header);

    // Also recalculate on viewport resize.
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
      document.documentElement.style.removeProperty("--maya-header-height");
    };
  }, []);

  const fetchProfile = useCallback(async () => {
    const mainTenantId = sessionStorage.getItem("mainTenantId");

    if (!mainTenantId) return;

    try {
      const response = await getUserProfile(mainTenantId);
      setProfileData(response?.data || null);
    } catch (error) {
      console.error("Error fetching user profile for email banner:", error);
    }
  }, []);

  const handleVerifyEmail = useCallback(async () => {
    if (!profileRef.current?.email) {
      MessageManager.showToast("error", "Email address not found.");
      return;
    }

    try {
      const response = await sendVerificationEmailApi(profileRef.current.email);

      if (response?.code === 200) {
        MessageManager.showToast(
          "success",
          "Verification email sent successfully!",
        );
      } else if (response?.code === 429) {
        MessageManager.showToast(
          "error",
          "Maximum verification email attempts reached. Please try again after 24 hours.",
        );
      } else {
        MessageManager.showToast("error", "Failed to send verification email.");
      }
    } catch (error) {
      console.error("Error sending verification email:", error);

      if (
        error?.response?.status === 429 ||
        error?.status === 429 ||
        error?.code === 429
      ) {
        MessageManager.showToast(
          "error",
          "Limit reached for the day. Unable to send verification email",
        );
        return;
      }

      MessageManager.showToast("error", "Failed to send verification email.");
    }
  }, []);

  useEffect(() => {
    profileRef.current = profileData;
  }, [profileData]);

  useEffect(() => {
    fetchProfile();

    return () => {
      if (bannerRef.current) {
        MessageManager.removeBanner(bannerRef.current);
        bannerRef.current = null;
      }
    };
  }, [fetchProfile]);

  const isEmailVerified =
    profileData?.verifiedEmail === true || profileData?.verified_email === true;
  const emailVerificationStatus = isEmailVerified
    ? "verified"
    : profileData?.email_verification_status || "unverified";

  useEffect(() => {
    if (!profileData) return;

    const shouldShowBanner =
      !isEmailVerified && emailVerificationStatus !== "locked";

    if (shouldShowBanner && !bannerRef.current) {
      const bannerId = MessageManager.showBanner(
        "warning",
        "Your email is not verified. You will not receive any notifications until you verify your email.",
        [
          {
            label: "Verify Email",
            type: "primary",
            onClick: handleVerifyEmail,
          },
          {
            label: "Dismiss",
            type: "secondary",
            onClick: () => {
              if (bannerRef.current) {
                MessageManager.removeBanner(bannerRef.current);
                bannerRef.current = null;
              }
            },
          },
        ],
        {
          className: "email-verification-banner",
        },
      );

      bannerRef.current = bannerId;
    } else if (!shouldShowBanner && bannerRef.current) {
      MessageManager.removeBanner(bannerRef.current);
      bannerRef.current = null;
    }
  }, [
    emailVerificationStatus,
    handleVerifyEmail,
    isEmailVerified,
    profileData,
  ]);

  return (
    <>
      <div>
        <Header />
        <div
          id="message-banner-container"
          className="msg-banner-container"
        ></div>
        <main>
          <Outlet />
        </main>
        <Footer />
        {ENABLE_CHAT_ASSISTANT && (
          <ChatAssistant isOpen={isChatOpen} onToggle={toggleChat} />
        )}
      </div>
    </>
  );
}

export default Layout;
